/**
 * NotificationBell Component
 *
 * Displays a bell icon with a badge showing unread notification count.
 * Features smooth animations and refined visual feedback.
 */

import { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useUnreadCount } from '@/services/queries/notifications';
import { NotificationPanel } from './notification-panel';
import { cn } from '@/lib/utils/cn';

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const { data: unreadCount = 0, isSuccess: unreadCountReady } = useUnreadCount();
  const prevUnreadCountRef = useRef<number | null>(null);
  const unreadBaselineReadyRef = useRef(false);

  const displayCount = unreadCount.toString();

  // Auto-open the panel when new notifications arrive while using the app.
  // Do not treat the first successful fetch (e.g. 0 → N after refresh) as "new" mail.
  useEffect(() => {
    if (!unreadCountReady) return;

    if (!unreadBaselineReadyRef.current) {
      unreadBaselineReadyRef.current = true;
      prevUnreadCountRef.current = unreadCount;
      return;
    }

    const prev = prevUnreadCountRef.current;
    prevUnreadCountRef.current = unreadCount;

    if (prev !== null && unreadCount > prev && !isOpen) {
      setIsOpen(true);
      const timeout = window.setTimeout(() => {
        setIsOpen(false);
      }, 6000);
      return () => window.clearTimeout(timeout);
    }
  }, [unreadCountReady, unreadCount, isOpen]);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'group relative rounded-full p-2.5',
          'transition-all duration-300 ease-out',
          'hover:bg-gray-100 dark:hover:bg-gray-800',
          'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2',
          isOpen && 'bg-gray-100 dark:bg-gray-800'
        )}
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        {/* Bell icon with subtle animation on hover */}
        <Bell className={cn(
          'h-5 w-5 text-gray-600 dark:text-gray-400',
          'transition-transform duration-300 ease-out',
          'group-hover:scale-110',
          unreadCount > 0 && 'group-hover:animate-[swing_1s_ease-in-out]'
        )} />

        {/* Animated badge with glow effect */}
        {unreadCount > 0 && (
          <span className={cn(
            'absolute -right-0.5 -top-0.5',
            'flex h-5.5 min-w-5.5 items-center justify-center',
            'rounded-full bg-gradient-to-br from-red-500 to-red-600',
            'px-1.5 text-[10px] font-bold text-white',
            'shadow-lg shadow-red-500/30',
            'transition-all duration-300 ease-out',
            'animate-in zoom-in fade-in slide-in-from-top-2',
            unreadCount > 0 && 'animate-pulse-slow'
          )}>
            {displayCount}
          </span>
        )}
      </button>

      {/* Panel with animation */}
      {isOpen && (
        <div className="animate-in fade-in slide-in-from-top-2 duration-300 ease-out">
          <NotificationPanel onClose={() => setIsOpen(false)} />
        </div>
      )}
    </div>
  );
}
